import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useFloorPlan, useUpdateTablePositions, useReserveTable } from '@/hooks/useApi';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { Spinner } from '@/components/Spinner';
import { toast } from '@/components/ui/Toast';
import { toPersianDigits } from '@/lib/jalali';
export function FloorPlan({ canEdit }) {
    const { t } = useTranslation();
    const [from] = useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
    });
    const [to] = useState(() => {
        const d = new Date();
        d.setHours(23, 59, 59, 999);
        return d.toISOString();
    });
    const planQ = useFloorPlan(from, to);
    const updatePositions = useUpdateTablePositions();
    const reserve = useReserveTable();
    const [editMode, setEditMode] = useState(false);
    const [tables, setTables] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    useEffect(() => {
        if (planQ.data) {
            setTables(planQ.data.map((t) => ({
                ...t,
                posX: t.posX ?? Math.random() * 80,
                posY: t.posY ?? Math.random() * 80,
                width: t.width ?? 80,
                height: t.height ?? 60,
            })));
        }
    }, [planQ.data]);
    const canvasRef = useRef(null);
    const onDragEnd = (id, x, y) => {
        const c = canvasRef.current;
        if (!c)
            return;
        const rect = c.getBoundingClientRect();
        const px = Math.max(0, Math.min(95, (x / rect.width) * 100));
        const py = Math.max(0, Math.min(95, (y / rect.height) * 100));
        setTables((ts) => ts.map((t) => (t.id === id ? { ...t, posX: px, posY: py } : t)));
    };
    const saveLayout = async () => {
        try {
            await updatePositions.mutateAsync(tables.map((t) => ({ id: t.id, posX: t.posX, posY: t.posY, width: t.width, height: t.height })));
            toast.success(t('tables.saveLayout'));
            setEditMode(false);
        }
        catch (e) {
            toast.error(e?.response?.data?.error || 'خطا');
        }
    };
    const sections = useMemo(() => Array.from(new Set(tables.map((t) => t.section))), [tables]);
    if (planQ.isLoading)
        return _jsx(Spinner, {});
    const selected = selectedId ? tables.find((t) => t.id === selectedId) : null;
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-3", children: [_jsxs("div", { className: "flex items-center gap-2 text-sm", children: [_jsx(Legend, { color: "bg-green-500", label: t('tables.available') }), _jsx(Legend, { color: "bg-red-500", label: t('tables.reserved') }), _jsxs("span", { className: "ml-3 text-xs text-gray-500", children: [sections.length, " ", t('tables.section')] })] }), canEdit && (editMode
                        ? _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: "secondary", onClick: () => setEditMode(false), children: t('common.cancel') }), _jsx(Button, { onClick: saveLayout, loading: updatePositions.isPending, children: t('tables.saveLayout') })] })
                        : _jsx(Button, { variant: "secondary", onClick: () => setEditMode(true), children: t('tables.editLayout') }))] }), _jsx("div", { ref: canvasRef, className: "relative h-[520px] w-full overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-950", children: tables.map((tbl) => {
                    const reserved = tbl.reservations.length > 0;
                    const color = reserved ? 'bg-red-500/80 border-red-700' : 'bg-green-500/80 border-green-700';
                    return (_jsx(motion.div, { drag: editMode, dragMomentum: false, onDragEnd: (_, info) => onDragEnd(tbl.id, info.point.x - (canvasRef.current?.getBoundingClientRect().left ?? 0), info.point.y - (canvasRef.current?.getBoundingClientRect().top ?? 0)), onClick: () => !editMode && setSelectedId(tbl.id), style: { left: `${tbl.posX}%`, top: `${tbl.posY}%`, width: tbl.width ?? 80, height: tbl.height ?? 60 }, className: `absolute flex cursor-pointer items-center justify-center border-2 text-xs font-bold text-white shadow-md transition ${color} ${tbl.shape === 'CIRCLE' ? 'rounded-full' : 'rounded-lg'} ${editMode ? 'cursor-grab' : 'hover:scale-105'}`, whileHover: editMode ? {} : { scale: 1.05 }, children: _jsxs("div", { className: "text-center leading-tight", children: [_jsxs("div", { children: ["#", toPersianDigits(tbl.number)] }), _jsxs("div", { className: "text-[10px] font-normal opacity-90", children: [toPersianDigits(tbl.seats), " ", t('tables.seats')] })] }) }, tbl.id));
                }) }), _jsx(TableDetailModal, { open: !!selected, onClose: () => setSelectedId(null), table: selected, onReserve: async (input) => {
                    if (!selected)
                        return;
                    try {
                        await reserve.mutateAsync({ tableId: selected.id, ...input });
                        toast.success(t('tables.reserve'));
                        setSelectedId(null);
                    }
                    catch (e) {
                        toast.error(e?.response?.data?.error || 'خطا');
                    }
                } })] }));
}
function Legend({ color, label }) {
    return (_jsxs("span", { className: "inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs dark:bg-gray-900", children: [_jsx("span", { className: `h-2 w-2 rounded-full ${color}` }), " ", label] }));
}
function TableDetailModal({ open, onClose, table, onReserve }) {
    const { t } = useTranslation();
    const [reservedAt, setReservedAt] = useState('');
    const [partySize, setPartySize] = useState(2);
    const [notes, setNotes] = useState('');
    useEffect(() => {
        if (open) {
            const next = new Date();
            next.setMinutes(0, 0, 0);
            next.setHours(next.getHours() + 1);
            setReservedAt(next.toISOString().slice(0, 16));
            setPartySize(2);
            setNotes('');
        }
    }, [open]);
    if (!table)
        return null;
    return (_jsx(Modal, { open: open, onClose: onClose, title: `${t('tables.reserve')} — #${toPersianDigits(table.number)} (${table.section})`, size: "lg", footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "secondary", onClick: onClose, children: t('common.cancel') }), _jsx(Button, { onClick: () => onReserve({ reservedAt: new Date(reservedAt).toISOString(), partySize, notes: notes || undefined }), disabled: !reservedAt || partySize > table.seats, children: t('tables.reserve') })] }), children: _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "grid gap-3 sm:grid-cols-2", children: [_jsx(Field, { label: t('tables.reserveTime'), children: _jsx(Input, { type: "datetime-local", value: reservedAt, onChange: (e) => setReservedAt(e.target.value), dir: "ltr" }) }), _jsx(Field, { label: t('tables.partySize'), error: partySize > table.seats ? t('tables.capacityExceeded') : undefined, children: _jsx(Input, { type: "number", min: 1, max: table.seats, value: partySize, onChange: (e) => setPartySize(Number(e.target.value)) }) }), _jsx("div", { className: "sm:col-span-2", children: _jsx(Field, { label: t('common.notes'), children: _jsx(Input, { value: notes, onChange: (e) => setNotes(e.target.value) }) }) })] }), table.reservations.length > 0 && (_jsxs("div", { children: [_jsx("h4", { className: "mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300", children: "\u0631\u0632\u0631\u0648\u0647\u0627\u06CC \u0641\u0639\u0644\u06CC \u0627\u06CC\u0646 \u0645\u06CC\u0632" }), _jsx("ul", { className: "space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-2 text-sm dark:border-gray-700 dark:bg-gray-900", children: table.reservations.map((r) => (_jsxs("li", { className: "flex items-center justify-between", children: [_jsxs("span", { children: [new Date(r.reservedAt).toLocaleString('fa-IR'), " \u2014 ", r.user?.name ?? r.guestUser?.name ?? r.guestUser?.phone ?? '—'] }), _jsxs("span", { className: "text-xs text-gray-500", children: [toPersianDigits(r.partySize), " \u0646\u0641\u0631"] })] }, r.id))) })] }))] }) }));
}
